import { useEffect, useRef } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import defaultConfig from '../../consts/defaultConfig'
import { IModals } from '../../interfaces'
import { generateDataDogContext } from '../../utils/dataDog'
import useAppSelector from '../useAppSelector'

interface UnmountedModalReportArgs {
  mountedNames: string[]
  containerUid: string | undefined
  /** The route's key into `config.containers`, which `defaultConfig` shares. */
  containerName: string | undefined
  /** The container's unprocessed `modals`, compared by reference. */
  listedModals: unknown
}

const shownNames = (modalStatus: IModals | undefined) =>
  Object.keys(modalStatus || {}).filter((name) => modalStatus?.[name]?.show)

/**
 * `'default'` only for the very array `defaultConfig` authors: `deepMerge`
 * keeps that reference when the remote omits `modals`, and a stored copy, even
 * an identical one, is a new array. Tells a cinnamon gap from an SCS leak.
 */
const listSource = (containerName: string | undefined, listed: unknown) => {
  const { containers } = defaultConfig

  const defaultList =
    containerName && Object.hasOwn(containers, containerName)
      ? containers[containerName]?.modals
      : undefined
  return Array.isArray(listed) && listed === defaultList ? 'default' : 'config'
}

/**
 * Reports an `openModal` for a name this template did not mount, which shows
 * nothing (COM-1385). Rendering is unchanged: a storefront may unlist a modal
 * on purpose. Reports once per flip to shown, unlatched like the other config
 * reports. The first run only records what is already shown, since nothing
 * clears modal flags on navigation; an open landing in the same commit as the
 * mount is missed.
 */
export const useUnmountedModalReport = ({
  mountedNames,
  containerUid,
  containerName,
  listedModals,
}: UnmountedModalReportArgs) => {
  const modalStatus = useAppSelector((state) => state.defaultSlice.modals)
  const storeName = useAppSelector((state) => state.defaultSlice.storeName)
  const shownRef = useRef<Set<string> | null>(null)

  useEffect(() => {
    const shown = shownNames(modalStatus)
    const previous = shownRef.current
    shownRef.current = new Set(shown)
    if (!previous) return

    shown
      .filter((name) => !previous.has(name) && !mountedNames.includes(name))
      .forEach((name) => {
        datadogRum.addError(
          new Error(
            `modal ${JSON.stringify(name)} opened but container ${JSON.stringify(
              containerUid,
            )} does not list it, so nothing shows`,
          ),
          generateDataDogContext({
            title: 'modal opened but not mounted',
            extraInfo: {
              function: 'useUnmountedModalReport',
              modal: name,
              container: containerUid,
              listed: mountedNames,
              source: listSource(containerName, listedModals),
              storeName,
            },
          }),
        )
      })
  }, [
    modalStatus,
    mountedNames,
    containerUid,
    containerName,
    listedModals,
    storeName,
  ])
}
